import type { UnpackedGraphemeStrokes } from "#grapheme-strokes";
import { parseStrokeMedianPolyline } from "#parse-stroke-median-polyline";
import { typedArrayLength } from "@-/com.npmjs.typed-array-length";
import {
  flattenSegmentNums,
  parseSvgPathToAbsoluteRoundedSegments,
  segmentCmdAsciiList,
  svgPathSegmentsToD,
  unflattenSegmentNums,
  type SvgPathSegment,
} from "#svg-path-codec";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

const FORMAT_VERSION = 1 as const;

function argCountForSvgCommand(cmd: string): number {
  switch (cmd) {
    case "M":
    case "L": {
      return 2;
    }
    case "Q": {
      return 4;
    }
    case "C": {
      return 6;
    }
    case "H":
    case "V": {
      return 1;
    }
    case "Z": {
      return 0;
    }
    default: {
      return 0;
    }
  }
}

function emptySegmentsFromCmdAsciiList(cmdAsciiList: number[]): SvgPathSegment[] {
  return cmdAsciiList.map((code) => {
    const cmd = String.fromCodePoint(code);
    const nums = Array.from<number>({ length: argCountForSvgCommand(cmd) }).fill(0);
    return { cmd, nums };
  });
}

function zigzagEncode(n: number): number {
  return n < 0 ? -2 * n - 1 : 2 * n;
}

function zigzagDecode(n: number): number {
  return (n & 1) === 1 ? -((n + 1) >>> 1) : n >>> 1;
}

class BinaryWriter {
  private buffer: Uint8Array = new Uint8Array(64);
  private offset = 0;

  private ensure(extra: number): void {
    const needed = this.offset + extra;
    const bufferLength = typedArrayLength(this.buffer);
    if (needed <= bufferLength) {
      return;
    }
    let nextLength = bufferLength * 2;
    while (nextLength < needed) {
      nextLength *= 2;
    }
    const next = new Uint8Array(nextLength);
    next.set(this.buffer);
    this.buffer = next;
  }

  writeByte(byte: number): void {
    this.ensure(1);
    this.buffer[this.offset++] = byte & 255;
  }

  writeNumberArrayBytes(bytes: readonly number[]): void {
    this.ensure(arrayLength(bytes));
    for (const byte of bytes) {
      this.buffer[this.offset++] = byte & 255;
    }
  }

  writeTypedArrayBytes(bytes: Uint8Array): void {
    this.ensure(typedArrayLength(bytes));
    for (const byte of bytes) {
      this.buffer[this.offset++] = byte & 255;
    }
  }

  writeUvarint(value: number): void {
    if (!Number.isInteger(value) || value < 0) {
      throw new RangeError(`uvarint must be a non-negative integer, got ${value}`);
    }
    let remaining = value;
    while (remaining >= 0x80) {
      this.writeByte((remaining & 127) | 128);
      remaining = Math.floor(remaining / 128);
    }
    this.writeByte(remaining);
  }

  writeSvarint(value: number): void {
    this.writeUvarint(zigzagEncode(value));
  }

  toUint8Array(): Uint8Array {
    return this.buffer.slice(0, this.offset);
  }
}

class BinaryReader {
  private readonly buffer: Uint8Array;
  private offset = 0;

  constructor(buffer: Uint8Array) {
    this.buffer = buffer;
  }

  hasMore(): boolean {
    return this.offset < typedArrayLength(this.buffer);
  }

  readByte(): number {
    if (this.offset >= typedArrayLength(this.buffer)) {
      throw new RangeError("Unexpected end of stroke blob");
    }
    const byte = this.buffer[this.offset++];
    if (byte === undefined) {
      throw new RangeError("Unexpected end of stroke blob");
    }
    return byte;
  }

  readBytes(count: number): Uint8Array {
    if (this.offset + count > typedArrayLength(this.buffer)) {
      throw new RangeError("Unexpected end of stroke blob");
    }
    const slice = this.buffer.subarray(this.offset, this.offset + count);
    this.offset += count;
    return slice;
  }

  readUvarint(): number {
    let result = 0;
    let shift = 1;
    while (true) {
      const byte = this.readByte();
      result += (byte & 127) * shift;
      if ((byte & 128) === 0) {
        return result;
      }
      shift *= 128;
      if (shift > 2 ** 53) {
        throw new RangeError("uvarint exceeds safe integer range");
      }
    }
  }

  readSvarint(): number {
    return zigzagDecode(this.readUvarint());
  }
}

function encodeAxisStreamResiduals(writer: BinaryWriter, values: number[]): void {
  for (let i = 0; i < arrayLength(values); i++) {
    const value = values[i];
    if (value === undefined) {
      throw new RangeError("axis stream residual value out of range");
    }
    let prediction: number;
    if (i === 0) {
      prediction = 0;
    } else if (i === 1) {
      const prev = values[0];
      if (prev === undefined) {
        throw new RangeError("axis stream residual value out of range");
      }
      prediction = prev;
    } else {
      const prev = values[i - 1];
      const prevPrev = values[i - 2];
      if (prev === undefined || prevPrev === undefined) {
        throw new RangeError("axis stream residual value out of range");
      }
      prediction = 2 * prev - prevPrev;
    }
    writer.writeSvarint(value - prediction);
  }
}

function decodeAxisStreamResiduals(reader: BinaryReader, count: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const residual = reader.readSvarint();
    let prediction: number;
    if (i === 0) {
      prediction = 0;
    } else if (i === 1) {
      const prev = out[0];
      if (prev === undefined) {
        throw new RangeError("axis stream residual value out of range");
      }
      prediction = prev;
    } else {
      const prev = out[i - 1];
      const prevPrev = out[i - 2];
      if (prev === undefined || prevPrev === undefined) {
        throw new RangeError("axis stream residual value out of range");
      }
      prediction = 2 * prev - prevPrev;
    }
    out.push(prediction + residual);
  }
  return out;
}

function writeAxisStreamForPath(writer: BinaryWriter, cmdAscii: number[], flat: number[]): void {
  // Path nums interleave x and y for M/L/Q/C; H and V emit a single coord with no fixed axis.
  // To maximize predictor effectiveness, we separate even-index and odd-index nums into two
  // streams (this matches xy interleaving for M/L/Q/C; for H/V it still works as two parallel
  // monotonic-ish streams since H args are x-like and V args are y-like and they alternate).
  // Predictor + zigzag varints are robust to the mismatch, so this is safe and lossless.
  void cmdAscii;
  const evens: number[] = [];
  const odds: number[] = [];
  for (const [index, value] of flat.entries()) {
    (index % 2 === 0 ? evens : odds).push(value);
  }
  encodeAxisStreamResiduals(writer, evens);
  encodeAxisStreamResiduals(writer, odds);
}

function readAxisStreamForPath(
  reader: BinaryReader,
  cmdAscii: number[],
  totalCount: number,
): number[] {
  void cmdAscii;
  const evenCount = Math.ceil(totalCount / 2);
  const oddCount = Math.floor(totalCount / 2);
  const evens = decodeAxisStreamResiduals(reader, evenCount);
  const odds = decodeAxisStreamResiduals(reader, oddCount);
  const out: number[] = [];
  for (let i = 0; i < totalCount; i++) {
    const value = i % 2 === 0 ? evens[i / 2] : odds[(i - 1) / 2];
    if (value === undefined) {
      throw new RangeError("axis stream index out of range");
    }
    out.push(value);
  }
  return out;
}

function encodeStrokeToWriter(writer: BinaryWriter, path: string, medianUnknown: unknown): boolean {
  const segments = parseSvgPathToAbsoluteRoundedSegments(path);
  if (hasLength(segments, 0)) {
    return false;
  }
  const medians = parseStrokeMedianPolyline(medianUnknown);
  if (hasLength(medians, 0)) {
    return false;
  }
  const cmdAscii = segmentCmdAsciiList(segments);
  const flatNums = flattenSegmentNums(segments);

  writer.writeUvarint(arrayLength(cmdAscii));
  writer.writeNumberArrayBytes(cmdAscii);
  writer.writeUvarint(arrayLength(flatNums));
  writeAxisStreamForPath(writer, cmdAscii, flatNums);

  writer.writeUvarint(arrayLength(medians));
  const xs: number[] = [];
  const ys: number[] = [];
  for (const [x, y] of medians) {
    xs.push(x);
    ys.push(y);
  }
  encodeAxisStreamResiduals(writer, xs);
  encodeAxisStreamResiduals(writer, ys);
  return true;
}

function decodeStrokeFromReader(reader: BinaryReader): UnpackedGraphemeStrokes | null {
  const cmdCount = reader.readUvarint();
  const cmdAscii = [...reader.readBytes(cmdCount)];
  const numCount = reader.readUvarint();

  const template = emptySegmentsFromCmdAsciiList(cmdAscii);
  const expectedFlatLength = template.reduce((sum, seg) => sum + arrayLength(seg.nums), 0);
  if (expectedFlatLength !== numCount) {
    return null;
  }

  const flatNums = readAxisStreamForPath(reader, cmdAscii, numCount);
  const filled = unflattenSegmentNums(template, flatNums);
  if (!hasLength(filled, arrayLength(template))) {
    return null;
  }
  const path = svgPathSegmentsToD(filled);

  const medianCount = reader.readUvarint();
  const xs = decodeAxisStreamResiduals(reader, medianCount);
  const ys = decodeAxisStreamResiduals(reader, medianCount);
  const medians: [number, number][] = [];
  for (let i = 0; i < medianCount; i++) {
    const x = xs[i];
    const y = ys[i];
    if (x === undefined || y === undefined) {
      return null;
    }
    medians.push([x, y]);
  }

  return { medians, path };
}

/**
 * Encodes a glyph's stroke list into a compact lossless binary blob.
 *
 * Format: `[version: u8][strokeCount: uvarint][per stroke: cmds + path residuals + median
 * residuals]`. Numeric streams use linear-prediction residuals (`pred = 2*prev - prevPrev`) on
 * deinterleaved x/y axes, encoded as zigzag varints. Smooth strokes produce residuals clustered
 * near zero, so most numbers fit in a single byte.
 */
export function encodeCompactGraphemeStrokes(strokeList: UnpackedGraphemeStrokes[]): Uint8Array {
  const writer = new BinaryWriter();
  writer.writeByte(FORMAT_VERSION);

  const encodedStrokes: Uint8Array[] = [];
  for (const stroke of strokeList) {
    const strokeWriter = new BinaryWriter();
    const ok = encodeStrokeToWriter(strokeWriter, stroke.path, stroke.medians);
    if (!ok) {
      continue;
    }
    encodedStrokes.push(strokeWriter.toUint8Array());
  }

  writer.writeUvarint(arrayLength(encodedStrokes));
  for (const strokeBytes of encodedStrokes) {
    writer.writeTypedArrayBytes(strokeBytes);
  }

  return writer.toUint8Array();
}

/** Decodes a blob produced by {@link encodeCompactGraphemeStrokes}. */
export function decodeCompactGraphemeStrokes(bytes: Uint8Array): UnpackedGraphemeStrokes[] | null {
  if (typedArrayLength(bytes) === 0) {
    return null;
  }
  const reader = new BinaryReader(bytes);
  const version = reader.readByte();
  if (version !== FORMAT_VERSION) {
    return null;
  }
  const strokeCount = reader.readUvarint();
  const out: UnpackedGraphemeStrokes[] = [];
  for (let i = 0; i < strokeCount; i++) {
    const stroke = decodeStrokeFromReader(reader);
    if (stroke === null) {
      return null;
    }
    out.push(stroke);
  }
  return out;
}
